import { ArrowRight, MessageSquare, Plug } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Section, SectionHeader } from "@/components/marketing/Section";
import { Button } from "@/components/ui/Button";
import { appSignInUrl } from "@/lib/env";
import { preserveSearch } from "@/lib/nav";
import { useDesktopPlatform } from "@/lib/useDesktopPlatform";

const tasks = [
  {
    title: "Make a research question specific",
    example: "Compare these companies and show where the evidence is incomplete.",
    detail:
      "Name the instruments, time horizon, and decision you are working toward. Check the sources and data coverage before building a thesis around the answer.",
    guide: "data",
    label: "Data and coverage",
  },
  {
    title: "Turn an idea into a test",
    example: "Define this strategy, then test its rules under realistic costs.",
    detail:
      "Keep the strategy version, test period, and assumptions together. Look at drawdown, trade count, and the shape of the results before deciding what to investigate next.",
    guide: "backtesting",
    label: "Backtests and results",
  },
  {
    title: "Bring the decision back to your account",
    example: "Evaluate this trade against my selected account and prepare a proposal.",
    detail:
      "Review the account, sizing, order details, and applicable Risk Controls. A trade proposal is a draft; the approval card is where you decide whether to proceed.",
    guide: "trading",
    label: "Trade proposals and orders",
  },
  {
    title: "Review what actually happened",
    example: "Help me review the outcome and record what I learned.",
    detail:
      "Use Trade Logs and Journal to connect the decision with its outcome. Keep the reason for a trade alongside the result so your next review has context.",
    guide: "activity",
    label: "Trade Logs and Journal",
  },
];

export function Product() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const showDesktopDownload = useDesktopPlatform() !== "unknown";
  const path = (to: string) => preserveSearch(to, search);

  return (
    <>
      <Section>
        <SectionHeader
          pageTitle
          title="Put your next decision to *work*"
          subtitle="A practical guide to working with Mavori: choose how you reach the agent, give it a focused task, and review the evidence before you act."
        />
        <div className="mt-8 flex flex-wrap gap-3">
          <Button
            {...(showDesktopDownload
              ? { to: path("/download") }
              : { href: appSignInUrl("", new URLSearchParams(search).get("ref") ?? undefined) })}
            trailing={<ArrowRight className="h-4 w-4" aria-hidden />}
          >
            {showDesktopDownload ? "Get Mavori" : t("download.openWeb")}
          </Button>
          <Button to={path("/docs/getting-started")} variant="secondary">
            Start with the guide
          </Button>
        </div>
      </Section>

      <Section className="border-y border-border bg-card/30">
        <SectionHeader title="Choose your starting point" />
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {[
            {
              icon: MessageSquare,
              title: "Mavori's hosted agent",
              text: "Start a conversation in Mavori with no external AI client to configure. Research, strategy work, and portfolio context are available through the product's own agent.",
              label: "Meet the agent",
              guide: "agent",
            },
            {
              icon: Plug,
              title: "Your AI client, connected by MCP",
              text: "Use Mavori's capabilities from a compatible AI client through Model Context Protocol (MCP). MCP is a paid capability; review your plan and authorize the client from Mavori's connection settings.",
              label: "Explore MCP connections",
              guide: "mcp",
            },
          ].map(({ icon: Icon, title, text, label, guide }) => (
            <article key={guide} className="rounded-[2rem] border border-border bg-card p-7">
              <Icon className="h-6 w-6 text-primary" aria-hidden />
              <h3 className="mt-5 text-xl font-semibold text-foreground">{title}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">{text}</p>
              <Button className="mt-5 px-0" variant="link" to={path(`/docs/${guide}`)}>
                {label}
              </Button>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader
          title="Give each task a clear *next step*"
          subtitle="These example requests show how to frame the work. Answers depend on the data, account access, and capabilities available to you."
        />
        <ol className="mt-10 divide-y divide-border">
          {tasks.map((task, index) => (
            <li
              key={task.guide}
              className="grid gap-5 py-8 first:pt-0 md:grid-cols-[3rem_1fr_1fr] md:gap-8"
            >
              <span className="font-mono text-sm text-primary">0{index + 1}</span>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{task.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  “{task.example}”
                </p>
              </div>
              <div>
                <p className="leading-relaxed text-muted-foreground">{task.detail}</p>
                <Link
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  to={path(`/docs/${task.guide}`)}
                >
                  {task.label} <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-6 rounded-card border border-border bg-card p-6">
          <h3 className="font-semibold text-foreground">Keep the limits in view</h3>
          <p className="mt-2 leading-relaxed text-muted-foreground">
            AI responses can be wrong. Historical results do not predict future returns. Paper
            trading, live trading, and strategy deployment have different requirements; review the
            supported account and action before proceeding.
          </p>
          <Button className="mt-3 px-0" variant="link" to={path("/security")}>
            Read about controls and privacy
          </Button>
        </div>
      </Section>
    </>
  );
}
